// Página de emissão de NFe
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../../components/common/Card';
import { Button } from '../../../components/common/Button';
import { Input } from '../../../components/common/Input';
import { Select } from '../../../components/common/Select';
import { Table } from '../../../components/common/Table';
import { Breadcrumbs } from '../../../components/layout/Breadcrumbs';
import { Loading } from '../../../components/common/Loading';
import { Plus, Trash2, AlertCircle } from 'lucide-react';
import { formatCurrency } from '../../../utils/formatters';
import { useEmpresa } from '../../../contexts/EmpresaContext';
import { getClientes } from '../../../services/clientesService';
import { getProdutos } from '../../../services/produtosService';
import { createNotaFiscal } from '../../../services/notasFiscaisService';
import { supabase } from '../../../lib/supabase';
import type { Cliente, Produto, ItemNota as ItemNotaType } from '../../../types';

interface ItemNota {
  id: string;
  produto: Produto;
  quantidade: number;
  valorUnitario: number;
  valorTotal: number;
}

export function NFe() {
  const navigate = useNavigate();
  const { empresaSelecionada, contaId } = useEmpresa();
  const [destinatario, setDestinatario] = useState<Cliente | null>(null);
  const [buscaCliente, setBuscaCliente] = useState('');
  const [itens, setItens] = useState<ItemNota[]>([]);
  const [produtoSelecionado, setProdutoSelecionado] = useState('');
  const [quantidade, setQuantidade] = useState(1);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Carregar clientes e produtos do banco
  useEffect(() => {
    if (!empresaSelecionada) return;

    const carregarDados = async () => {
      setLoading(true);
      try {
        const [clientesData, produtosData] = await Promise.all([
          getClientes(empresaSelecionada.id),
          getProdutos(empresaSelecionada.id),
        ]);
        setClientes(clientesData);
        setProdutos(produtosData);
      } catch (err: any) {
        console.error('[NFe] Erro ao carregar dados:', err);
        setError('Erro ao carregar clientes e produtos');
      } finally {
        setLoading(false);
      }
    };

    carregarDados();
  }, [empresaSelecionada]);

  const clientesFiltrados = clientes.filter(c =>
    c.nome.toLowerCase().includes(buscaCliente.toLowerCase()) ||
    c.cpf?.includes(buscaCliente) ||
    c.cnpj?.includes(buscaCliente)
  );
  
  const adicionarItem = () => {
    const produto = produtos.find(p => p.id === produtoSelecionado);
    if (!produto) return;
    
    const novoItem: ItemNota = {
      id: Date.now().toString(),
      produto,
      quantidade,
      valorUnitario: produto.precoVenda,
      valorTotal: produto.precoVenda * quantidade,
    };
    
    setItens([...itens, novoItem]);
    setProdutoSelecionado('');
    setQuantidade(1);
  };
  
  const removerItem = (id: string) => {
    setItens(itens.filter(i => i.id !== id));
  };
  
  const subtotal = itens.reduce((acc, item) => acc + item.valorTotal, 0);
  const desconto = 0;
  const total = subtotal - desconto;

  // Gerar número e série da nota (simplificado - em produção deve vir do banco)
  const gerarNumeroSerie = () => {
    // Buscar última nota da empresa para gerar próximo número
    // Por enquanto, usar número baseado em timestamp
    const numero = Math.floor(Date.now() / 1000).toString().slice(-6);
    const serie = '1'; // Série padrão
    return { numero, serie };
  };

  // Buscar contaId da empresa se não estiver no contexto
  const getContaId = async (): Promise<string> => {
    if (contaId) return contaId;
    
    // Buscar contaId da empresa diretamente
    const { data, error } = await supabase
      .from('empresas')
      .select('conta_id')
      .eq('id', empresaSelecionada!.id)
      .single();
    
    if (error || !data?.conta_id) {
      throw new Error('Erro ao obter conta_id da empresa');
    }
    
    return data.conta_id;
  };

  // Salvar como rascunho
  const handleSalvarRascunho = async () => {
    if (!empresaSelecionada || !destinatario || itens.length === 0) {
      setError('Preencha todos os campos obrigatórios');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const contaIdFinal = await getContaId();
      const { numero, serie } = gerarNumeroSerie();

      // Converter itens para formato do banco
      const itensNota: ItemNotaType[] = itens.map(item => ({
        id: item.id,
        tipo: 'produto' as const,
        produtoId: item.produto.id,
        descricao: item.produto.descricao,
        quantidade: item.quantidade,
        unidadeMedida: item.produto.unidadeMedida,
        valorUnitario: item.valorUnitario,
        valorTotal: item.valorTotal,
        ncm: item.produto.ncm,
        cfop: undefined,
      }));

      // Criar nota fiscal como rascunho
      const nota = await createNotaFiscal(
        {
          tipo: 'NFe',
          numero,
          serie,
          emitente: empresaSelecionada,
          destinatario,
          dataEmissao: new Date().toISOString(),
          itens: itensNota,
          subtotal,
          desconto,
          total,
          status: 'rascunho',
        },
        contaIdFinal,
        empresaSelecionada.id
      );

      // Navegar para página de detalhes da nota
      navigate(`/app/notas/${nota.id}`);
    } catch (err: any) {
      console.error('[NFe] Erro ao salvar rascunho:', err);
      setError(err.message || 'Erro ao salvar rascunho');
    } finally {
      setSaving(false);
    }
  };

  // Emitir nota
  const handleEmitir = async () => {
    if (!empresaSelecionada || !destinatario || itens.length === 0) {
      setError('Preencha todos os campos obrigatórios');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const contaIdFinal = await getContaId();
      const { numero, serie } = gerarNumeroSerie();

      // Converter itens para formato do banco
      const itensNota: ItemNotaType[] = itens.map(item => ({
        id: item.id,
        tipo: 'produto' as const,
        produtoId: item.produto.id,
        descricao: item.produto.descricao,
        quantidade: item.quantidade,
        unidadeMedida: item.produto.unidadeMedida,
        valorUnitario: item.valorUnitario,
        valorTotal: item.valorTotal,
        ncm: item.produto.ncm,
        cfop: undefined,
      }));

      // Criar nota fiscal como emitida
      const nota = await createNotaFiscal(
        {
          tipo: 'NFe',
          numero,
          serie,
          emitente: empresaSelecionada,
          destinatario,
          dataEmissao: new Date().toISOString(),
          itens: itensNota,
          subtotal,
          desconto,
          total,
          status: 'emitida',
        },
        contaIdFinal,
        empresaSelecionada.id
      );

      // Navegar para página de detalhes da nota
      navigate(`/app/notas/${nota.id}`);
    } catch (err: any) {
      console.error('[NFe] Erro ao emitir nota:', err);
      setError(err.message || 'Erro ao emitir nota');
    } finally {
      setSaving(false);
    }
  };

  // Verificar se pode salvar/emitir
  const podeSalvar = empresaSelecionada && destinatario && itens.length > 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loading />
      </div>
    );
  }

  if (!empresaSelecionada) {
    return (
      <div className="space-y-6">
        <Card>
          <div className="text-center py-12">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-yellow-600" />
            <p className="text-gray-600 dark:text-gray-400">
              Selecione uma empresa para emitir notas fiscais
            </p>
          </div>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <Breadcrumbs items={[{ label: 'Emissão', path: '/app/emissao/nfe' }, { label: 'NFe' }]} />
      
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Emitir NFe</h1>
      </div>
      
      {/* Dados do Emitente */}
      <Card>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Dados do Emitente
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Input label="Razão Social" value={empresaSelecionada.razaoSocial} disabled />
          </div>
          <div>
            <Input label="CNPJ" value={empresaSelecionada.cnpj} disabled />
          </div>
          <div>
            <Input label="Email" value={empresaSelecionada.email} disabled />
          </div>
          <div>
            <Input label="Telefone" value={empresaSelecionada.telefone} disabled />
          </div>
        </div>
      </Card>
      
      {/* Dados do Destinatário */}
      <Card>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Dados do Destinatário
        </h2>
        <div className="mb-4">
          <Input
            label="Buscar Cliente"
            placeholder="Nome, CPF ou CNPJ"
            value={buscaCliente}
            onChange={(e) => setBuscaCliente(e.target.value)}
          />
          {buscaCliente && clientesFiltrados.length > 0 && (
            <div className="mt-2 border border-gray-200 dark:border-gray-700 rounded-lg max-h-48 overflow-y-auto">
              {clientesFiltrados.map((cliente) => (
                <button
                  key={cliente.id}
                  onClick={() => {
                    setDestinatario(cliente);
                    setBuscaCliente('');
                  }}
                  className="w-full px-4 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <p className="font-medium text-gray-900 dark:text-gray-100">{cliente.nome}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {cliente.tipo === 'F' ? cliente.cpf : cliente.cnpj}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>
        
        {destinatario && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <div>
              <Input label="Nome" value={destinatario.nome} disabled />
            </div>
            <div>
              <Input 
                label={destinatario.tipo === 'F' ? 'CPF' : 'CNPJ'} 
                value={destinatario.tipo === 'F' ? destinatario.cpf : destinatario.cnpj} 
                disabled 
              />
            </div>
            <div>
              <Input label="Email" value={destinatario.email} disabled />
            </div>
            <div>
              <Input label="Telefone" value={destinatario.telefone} disabled />
            </div>
            <div className="md:col-span-2">
              <Input 
                label="Endereço" 
                value={`${destinatario.endereco.logradouro}, ${destinatario.endereco.numero} - ${destinatario.endereco.bairro}, ${destinatario.endereco.cidade}/${destinatario.endereco.estado}`}
                disabled 
              />
            </div>
          </div>
        )}
      </Card>
      
      {/* Produtos */}
      <Card>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Produtos
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <Select
            label="Produto"
            value={produtoSelecionado}
            onChange={(e) => setProdutoSelecionado(e.target.value)}
            options={[
              { value: '', label: 'Selecione um produto' },
              ...produtos.map(p => ({ value: p.id, label: `${p.codigo} - ${p.descricao}` })),
            ]}
          />
          <Input
            label="Quantidade"
            type="number"
            value={quantidade.toString()}
            onChange={(e) => setQuantidade(Number(e.target.value))}
            min="1"
          />
          <div className="flex items-end">
            <Button onClick={adicionarItem} className="w-full">
              <Plus className="w-4 h-4 mr-2" />
              Adicionar
            </Button>
          </div>
        </div>
        
        {itens.length > 0 && (
          <Table
            data={itens}
            columns={[
              { key: 'produto', label: 'Produto', render: (item) => item.produto.descricao },
              { key: 'quantidade', label: 'Qtd', render: (item) => item.quantidade },
              { key: 'valorUnitario', label: 'Valor Unit.', render: (item) => formatCurrency(item.valorUnitario) },
              { key: 'valorTotal', label: 'Total', render: (item) => formatCurrency(item.valorTotal) },
              {
                key: 'actions',
                label: 'Ações',
                render: (item) => (
                  <button
                    onClick={() => removerItem(item.id)}
                    className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                ),
              },
            ]}
          />
        )}
      </Card>
      
      {/* Totais e Preview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Totais
          </h2>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Subtotal:</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{formatCurrency(subtotal)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Desconto:</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{formatCurrency(desconto)}</span>
            </div>
            <div className="flex justify-between pt-3 border-t border-gray-200 dark:border-gray-700">
              <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">Total:</span>
              <span className="text-lg font-bold text-primary-600 dark:text-primary-400">{formatCurrency(total)}</span>
            </div>
          </div>
        </Card>
        
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Preview da Nota
          </h2>
          <div className="space-y-2 text-sm">
            <p className="text-gray-600 dark:text-gray-400">
              <strong>Emitente:</strong> {empresaSelecionada.razaoSocial}
            </p>
            {destinatario && (
              <p className="text-gray-600 dark:text-gray-400">
                <strong>Destinatário:</strong> {destinatario.nome}
              </p>
            )}
            <p className="text-gray-600 dark:text-gray-400">
              <strong>Itens:</strong> {itens.length}
            </p>
            <p className="text-gray-600 dark:text-gray-400">
              <strong>Total:</strong> {formatCurrency(total)}
            </p>
          </div>
        </Card>
      </div>
      
      {/* Mensagem de erro */}
      {error && (
        <Card>
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-2 text-red-700 dark:text-red-400">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        </Card>
      )}

      {/* Ações */}
      <div className="flex gap-4 justify-end">
        <Button 
          variant="secondary" 
          onClick={handleSalvarRascunho}
          disabled={!podeSalvar || saving}
          isLoading={saving}
        >
          Salvar Rascunho
        </Button>
        <Button 
          variant="primary" 
          onClick={handleEmitir}
          disabled={!podeSalvar || saving}
          isLoading={saving}
        >
          Emitir Nota
        </Button>
      </div>
    </div>
  );
}
